<template>
    <v-form ref="demographicForm" v-model="currentDemographic.isValid">
        <img v-if="archimedes" src="@/assets/cat.gif" id="archimedes" />
        <v-container class="py-0 py-sm-0">
            <v-row>
                <v-col class="pl-10 py-0 py-sm-0" style="min-height: 445px;">
                    <div class="form-group mb-6">
                        <label for="First name">{{ currentModel.settings.localizedContent['reservation_first_name_label'] }}</label>
                        <v-text-field v-model="currentDemographic.formData.firstName"
                                      name="FirstName"
                                      background-color="white"
                                      id="firstName"
                                      ref="firstName"
                                      class="mt-1"
                                      autocomplete="off"
                                      :rules="firstNameRules"
                                      solo
                                      outlined
                                      validate-on-blur>
                        </v-text-field>
                        <v-icon class="input-valid" v-if="firstNameValid" color="secondary" v-bind:class="{'archimedes': archimedes}">{{archimedes ? 'mdi-cat' : 'mdi-check'}}</v-icon>
                    </div>
                    <div class="form-group mb-6">
                        <label for="LastName">{{ currentModel.settings.localizedContent['reservation_last_name_label'] }}</label>
                        <v-text-field v-model="currentDemographic.formData.lastName"
                                      name="LastName"
                                      id="lastName"
                                      ref="lastName"
                                      background-color="white"
                                      class="mt-1"
                                      autocomplete="off"
                                      :rules="lastNameRules"
                                      solo
                                      outlined
                                      validate-on-blur>
                        </v-text-field>
                        <v-icon class="input-valid" v-if="lastNameValid" color="secondary">mdi-check</v-icon>
                    </div>
                    <div class="form-group mb-6" v-if="currentModel.settings.useDateDobInputType">
                        <label for="Dob">{{ currentModel.settings.localizedContent['reservation_dob_label'] }}</label>
                        <v-text-field v-facade="'##/##/####'"
                                      v-model="currentDemographic.formData.dob"
                                      name="Dob"
                                      type="text"
                                      id="dob"
                                      class="mt-1"
                                      style="width: 240px;"
                                      ref="dob"
                                      placeholder="MM/DD/YYYY"
                                      inputmode="numeric"
                                      background-color="white"
                                      autocomplete="off"
                                      :rules="dobRules"
                                      solo
                                      outlined
                                      validate-on-blur>
                        </v-text-field>
                        <v-icon class="input-valid" v-if="dobValid" color="secondary">mdi-check</v-icon>
                    </div>
                    <div class="form-group mb-6" v-if="currentModel.settings.useApproxAgeDobInputType">
                        <label for="approxAge">{{ currentModel.settings.localizedContent['reservation_approx_age_label'] }}</label>
                        <v-select v-model="currentDemographic.formData.approxAge"
                                  :items="currentModel.settings.approximateAgeValues"
                                  :allow-overflow="false"
                                  class="mt-1"
                                  name="approxAge"
                                  id="approxAge"
                                  ref="approxAge"
                                  background-color="white"
                                  outlined>
                        </v-select>
                        <v-icon class="input-valid" v-if="currentDemographic.formData.approxAge" color="secondary">mdi-check</v-icon>
                    </div>
                    <div class="form-group mt-3" v-if="currentModel.settings.showPatientGender && currentModel.settings.patientGenders.length === 2">
                        <label style="display: block;">{{ currentModel.settings.localizedContent['reservation_sex_label'] }}</label>
                        <v-btn-toggle v-model="currentDemographic.formData.sex" class="has-2-toggles" :dense="$vuetify.breakpoint.xs">
                            <v-btn value="Male" large outlined color="primary" light>Male</v-btn>
                            <v-btn value="Female" large outlined color="primary" light>Female</v-btn>
                        </v-btn-toggle>
                        <v-icon class="input-valid" v-if="currentDemographic.formData.sex" color="secondary" style="top: -40px; left: -37px;">mdi-check</v-icon>
                    </div>
                    <div class="form-group mt-3" v-if="currentModel.settings.showPatientGender && currentModel.settings.patientGenders.length > 2">
                        <label style="display: block;">{{ currentModel.settings.localizedContent['reservation_sex_label'] }}</label>
                        <v-select v-model="currentDemographic.formData.sex"
                                  :items="currentModel.settings.patientGenders.map(x => x.genderName)"
                                  :allow-overflow="false"
                                  class="mt-1"
                                  name="PatientGender"
                                  id="patientGender"
                                  ref="patientGender"
                                  background-color="white"
                                  outlined>
                        </v-select>
                        <v-icon class="input-valid" v-if="currentDemographic.formData.sex" color="secondary" style="top: -70px; left: -37px;">mdi-check</v-icon>
                    </div>
                </v-col>
            </v-row>
        </v-container>
    </v-form>
</template>

<script>
    import moment from 'moment';
    import ContextualMixin from '../mixins/contextualMixin'

    export default {
        name: 'Demographic',
        mixins: [ContextualMixin],
        data() {
            return {
                firstNameRules: [
                    v => !!v || 'First name is required',
                    v => /^[a-zA-Z0-9 '-.´]+$/.test(v) || 'First name is invalid.',
                    v => isNaN(v) || 'First name is invalid'
                ],
                lastNameRules: [
                    v => !!v || 'Last name is required',
                    v => /^[a-zA-Z0-9 '-.´]+$/.test(v) || 'Last name is invalid.',
                    v => isNaN(v) || 'Last name is invalid'
                ],
                dobRules: [
                    v => !!v || 'Date of birth is required',
                    v => moment(v, 'MM/DD/YYYY', true).isValid() || 'Date of birth is invalid',
                    v => moment().diff(v, 'days') > 0 || 'Date of birth is invalid',
                    v => moment().diff(v, 'years', false) < 120 || 'Date of birth is invalid',
                    v => moment().diff(v, 'years', true) > 0 || 'Date of birth is invalid',
                    v => moment(v, 'MM/DD/YYYY', true).years() > 1000 || 'Date of birth is invalid',
                ]
            }
        },
        computed: {
            currentDemographic() {
                return this.$store.state.currentDemographic;
            },
            currentDemographicRef() {
                return this.$refs.demographicForm;
            },
            firstNameValid() {
                if (!this.currentDemographic.formData.firstName || !this.$refs.firstName)
                    return false;
                return this.$refs.firstName.validate();
            },
            lastNameValid() {
                if (!this.currentDemographic.formData.lastName || !this.$refs.lastName)
                    return false;
                return this.$refs.lastName.validate();
            },
            dobValid() {
                if (!this.currentDemographic.formData.dob || !this.$refs.dob)
                    return false;
                return this.$refs.dob.validate();
            },
            archimedes() {
                return this.currentDemographic.formData.firstName
                    && this.currentDemographic.formData.firstName.toLowerCase().includes('archimedes');
            }
        },
        watch: {
            'currentDemographic.formData.firstName': function (v) {
                if (!v || !v.length)
                    return;
                let fn = this.$store.state.currentDemographic.formData.firstName;
                if (v.charAt(0) !== v.charAt(0).toUpperCase())
                    this.$store.state.currentDemographic.formData.firstName =
                        fn.charAt(0).toUpperCase() + fn.slice(1);
            },
            'currentDemographic.formData.lastName': function (v) {
                if (!v || !v.length)
                    return;
                let ln = this.$store.state.currentDemographic.formData.lastName;
                if (v.charAt(0) !== v.charAt(0).toUpperCase())
                    this.$store.state.currentDemographic.formData.lastName =
                        ln.charAt(0).toUpperCase() + ln.slice(1);
            }
        }
    }
</script>
<style>
    #archimedes {
        position: fixed;
        bottom: 15px;
        left: -50%;
        z-index: 1;
        animation: run 4s infinite normal linear;
    }

    @-webkit-keyframes run {
        0% {
            left: -50%;
        }

        100% {
            left: 100%;
        }
    }

    @keyframes run {
        0% {
            left: -50%;
        }

        100% {
            left: 100%;
        }
    }
</style>